import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

interface MarketUpdate {
  category: string;
  title: string;
  summary: string;
  impact: 'high' | 'medium' | 'low';
  timestamp: string;
}

interface FAQ {
  question: string;
  answer: string;
}

interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface Testimonial {
  quote: string;
  author: string;
  position: string;
  company: string;
}

@Component({
  selector: 'app-daily-market-report',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <!-- Hero Section -->
    <section class="relative bg-gradient-to-br from-gray-900 to-blue-900 text-white py-16 md:py-24">
      <div class="absolute inset-0 bg-pattern opacity-20"></div>
      <div class="container mx-auto px-4 relative z-10">
        <div class="flex flex-col md:flex-row items-center">
          <div class="md:w-1/2 mb-10 md:mb-0">
            <div class="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-sm font-medium mb-6">
              <span class="w-2 h-2 rounded-full bg-teal-400 animate-pulse"></span>
              <span>Market Intelligence</span>
            </div>
            <h1 class="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Daily Market Report
            </h1>
            <p class="text-xl md:text-2xl mb-8 text-blue-100">
              Stay ahead with daily insights and analysis on market trends, pricing dynamics, and industry developments delivered through comprehensive reports tailored to your specific business needs.
            </p>
            <div class="flex flex-col sm:flex-row gap-4">
              <a href="#contact" class="bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 text-center">
                Request a Demo
              </a>
              <a href="#features" class="bg-transparent hover:bg-white/10 border border-white text-white font-bold py-3 px-6 rounded-lg transition duration-300 text-center">
                Explore Features
              </a>
            </div>
          </div>
          <div class="md:w-1/2">
            <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-2xl">
              <img src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&q=80&w=1000" 
                   alt="Daily Market Report Dashboard showing real-time market data" 
                   class="rounded-lg w-full">
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Key Features Section -->
    <section class="py-16 bg-gray-50" id="features">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Key Features</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Our Daily Market Report provides comprehensive market intelligence to help you make informed business decisions, anticipate market shifts, and identify strategic opportunities through expert analysis and data-driven insights.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          @for (feature of features; track feature.title) {
            <div class="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition duration-300 border border-gray-100">
              <div class="text-teal-600 text-4xl mb-4">
                <i class="fas {{ feature.icon }}"></i>
              </div>
              <h3 class="text-xl font-bold mb-3 text-gray-900">{{ feature.title }}</h3>
              <p class="text-gray-600">{{ feature.description }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- Market Updates Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Sample Market Updates</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Here's a preview of the type of market intelligence you'll receive in your Daily Market Report.
          </p>
        </div>

        <div class="max-w-4xl mx-auto">
          @for (update of marketUpdates; track update.title) {
            <div class="mb-6 bg-white rounded-xl p-6 shadow-lg border-l-4 border-teal-500">
              <div class="flex justify-between items-start mb-2">
                <div>
                  <span class="inline-block px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2">{{ update.category }}</span>
                  <span class="inline-block px-3 py-1 rounded-full text-xs font-medium 
                    {{ update.impact === 'high' ? 'bg-red-100 text-red-800' : 
                       update.impact === 'medium' ? 'bg-yellow-100 text-yellow-800' : 
                       'bg-green-100 text-green-800' }}">
                    {{ update.impact.charAt(0).toUpperCase() + update.impact.slice(1) }} Impact
                  </span>
                </div>
                <span class="text-sm text-gray-500">{{ update.timestamp }}</span>
              </div>
              <h3 class="text-xl font-bold mb-2 text-gray-900">{{ update.title }}</h3>
              <p class="text-gray-600">{{ update.summary }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- Testimonials Section -->
    <section class="py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">What Our Clients Say</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Hear from business leaders who rely on our Daily Market Report for critical market intelligence.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          @for (testimonial of testimonials; track testimonial.author) {
            <div class="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
              <div class="text-teal-600 text-4xl mb-4">
                <i class="fas fa-quote-left"></i>
              </div>
              <p class="text-gray-700 mb-6 italic">{{ testimonial.quote }}</p>
              <div class="flex items-center">
                <div class="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 font-bold mr-4">
                  {{ testimonial.author.charAt(0) }}
                </div>
                <div>
                  <p class="font-bold text-gray-900">{{ testimonial.author }}</p>
                  <p class="text-gray-600 text-sm">{{ testimonial.position }}, {{ testimonial.company }}</p>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-16 bg-white" id="faq">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Frequently Asked Questions</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Get answers to common questions about our Daily Market Report service.
          </p>
        </div>

        <div class="max-w-4xl mx-auto">
          @for (faq of faqs; track faq.question) {
            <div class="mb-6 border-b border-gray-200 pb-6">
              <h3 class="text-xl font-bold mb-3 text-gray-900">{{ faq.question }}</h3>
              <p class="text-gray-600">{{ faq.answer }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-16 bg-gradient-to-br from-teal-900 to-blue-900 text-white" id="contact">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto text-center">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">Ready to Stay Ahead of Market Trends?</h2>
          <p class="text-xl mb-8">
            Join industry leaders who rely on our Daily Market Report for critical business intelligence.
          </p>
          <div class="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg">
            <h3 class="text-2xl font-bold mb-6">Request a Demo</h3>
            <form class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium mb-2">First Name</label>
                <input type="text" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-teal-400">
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Last Name</label>
                <input type="text" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-teal-400">
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Company Email</label>
                <input type="email" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-teal-400">
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Company</label>
                <input type="text" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-teal-400">
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium mb-2">How can we help you?</label>
                <textarea rows="4" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-teal-400"></textarea>
              </div>
              <div class="md:col-span-2">
                <button type="submit" class="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300">
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .bg-pattern {
      background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    }
  `]
})
export class DailyMarketReportComponent {
  currentDate = new Date();

  marketUpdates: MarketUpdate[] = [
    {
      category: 'Pricing Trends',
      title: 'Raw Material Prices Surge in Asian Markets',
      summary: 'Key raw material prices have increased by 8.5% in Asian markets due to supply chain disruptions and increased demand. This trend is expected to impact manufacturing costs in the coming quarter.',
      impact: 'high',
      timestamp: '08:30 AM'
    },
    {
      category: 'Regulatory',
      title: 'New Environmental Regulations Announced in EU',
      summary: 'The European Union has announced new environmental regulations that will affect chemical manufacturing processes. Companies will have 18 months to comply with the new standards.',
      impact: 'medium',
      timestamp: '10:15 AM'
    },
    {
      category: 'Market Movement',
      title: 'Increased Demand for Sustainable Products',
      summary: 'Consumer demand for sustainable products has grown by 22% year-over-year, creating new opportunities for companies with eco-friendly offerings.',
      impact: 'medium',
      timestamp: '11:45 AM'
    },
    {
      category: 'Competitor Activity',
      title: 'Major Competitor Announces Expansion Plans',
      summary: 'A leading competitor has announced plans to expand production capacity by 30% over the next two years, potentially affecting market dynamics and pricing strategies.',
      impact: 'high',
      timestamp: '02:20 PM'
    }
  ];

  features: Feature[] = [
    {
      icon: 'fa-chart-line',
      title: 'Market Trend Analysis',
      description: 'Comprehensive analysis of market trends, price movements, and trading patterns with expert commentary on factors driving market dynamics and future outlook.'
    },
    {
      icon: 'fa-newspaper',
      title: 'Industry News Digest',
      description: 'Curated summary of the most important industry news, corporate announcements, and regulatory developments with analysis of potential market impact.'
    },
    {
      icon: 'fa-balance-scale',
      title: 'Supply-Demand Balance',
      description: 'Detailed assessment of supply and demand dynamics across key markets, including production updates, inventory levels, and consumption patterns.'
    },
    {
      icon: 'fa-globe',
      title: 'Global Market Coverage',
      description: 'Comprehensive coverage of markets across North America, Europe, Asia, and emerging economies, with insights on regional trends and cross-market correlations.'
    },
    {
      icon: 'fa-chart-bar',
      title: 'Price Analytics',
      description: 'In-depth analysis of price movements, volatility, and forecasts for commodities, raw materials, and finished products relevant to your industry.'
    },
    {
      icon: 'fa-calendar-alt',
      title: 'Economic Calendar',
      description: 'Forward-looking calendar of key economic events, data releases, and industry conferences that may impact market conditions and business planning.'
    }
  ];

  testimonials: Testimonial[] = [
    {
      quote: "The Daily Market Report has become an essential part of our morning routine. It provides us with the critical market intelligence we need to make informed decisions throughout the day.",
      author: "Thomas Reynolds",
      position: "Chief Procurement Officer",
      company: "Global Manufacturing Inc."
    },
    {
      quote: "What sets this report apart is the quality of analysis and the actionable insights. It's not just data—it's intelligence we can use to drive our business forward.",
      author: "Maria Garcia",
      position: "VP of Supply Chain",
      company: "Industrial Solutions Group"
    },
    {
      quote: "We've been able to anticipate market shifts and adjust our strategies accordingly thanks to the early indicators in the Daily Market Report. It's given us a significant competitive advantage.",
      author: "James Wilson",
      position: "Director of Strategic Planning",
      company: "Advanced Materials Corp"
    }
  ];

  faqs: FAQ[] = [
    {
      question: "How is the Daily Market Report delivered?",
      answer: "The Daily Market Report is delivered via email early each morning, typically by 6:00 AM in your local time zone. It's also available through our secure online portal and mobile app, where you can access current and historical reports."
    },
    {
      question: "Can the Daily Market Report be customized for our specific industry?",
      answer: "Yes, we tailor the Daily Market Report to focus on the markets, competitors, and trends most relevant to your industry and business needs. During the onboarding process, we work with you to identify your key areas of interest."
    },
    {
      question: "How is the information in the Daily Market Report gathered and verified?",
      answer: "Our team of industry analysts collects data from thousands of trusted sources, including industry publications, government reports, company announcements, and proprietary data sources. All information is verified through our rigorous fact-checking process before inclusion in the report."
    },
    {
      question: "Is historical data available in the Daily Market Report?",
      answer: "Yes, subscribers have access to an archive of past reports through our online portal. Additionally, our platform includes powerful analytics tools that allow you to track trends and changes over time."
    },
    {
      question: "How many people in my organization can receive the Daily Market Report?",
      answer: "We offer flexible subscription options that allow multiple team members to receive the report. Our enterprise plans include unlimited users within your organization."
    }
  ];
} 